import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, Input, Select } from '@/components/common';
import { getErrorMessage } from '@/services';
import {
  BASIS_LABEL,
  insurerReceiptsService,
  type AllocationInput,
  type QueueRow,
} from '@/services/insurerReceipts';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatCurrency, formatDate } from '@/utils';
import { ReconciliationTabs } from './ReconciliationTabs';

/**
 * #689: Reconciliation > Insurer receipts. The queue of bank credits not yet mapped; Map picks the
 * insurer (suggested from the UTR / earlier payments, or by hand) and spreads the credit over
 * broker-months with the TDS the insurer kept. Confirming posts to the ledger. Below: the position per
 * insurer-month (expected, insurer's claim, received, TDS, outstanding) and the confirmed receipts.
 */

const num = (v: string | undefined) => (v && !Number.isNaN(Number(v)) ? Number(v) : 0);
const monthOf = (d: string) => d.slice(0, 7);

interface Draft {
  line: QueueRow;
  insurer: string;
  rows: AllocationInput[];
  autoTds: boolean;
}

export function InsurerReceiptsPage() {
  const canEdit = useCan('reconciliation', 'edit');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [reasonFor, setReasonFor] = useState<{ kind: 'ignore' | 'unmatch'; line: string } | null>(null);
  const [reason, setReason] = useState('');
  const [positionInsurer, setPositionInsurer] = useState('');

  const insurers = useQuery({ queryKey: ['receiptInsurers'], queryFn: () => insurerReceiptsService.insurers() });
  const queue = useQuery({ queryKey: ['receiptQueue'], queryFn: () => insurerReceiptsService.queue() });
  const position = useQuery({
    queryKey: ['receiptPosition', positionInsurer],
    queryFn: () => insurerReceiptsService.position(positionInsurer || undefined),
  });
  const receipts = useQuery({ queryKey: ['insurerReceipts'], queryFn: () => insurerReceiptsService.receipts() });

  const refresh = () => {
    for (const k of ['receiptQueue', 'receiptPosition', 'insurerReceipts', 'bankStatementLines']) {
      qc.invalidateQueries({ queryKey: [k] });
    }
  };

  const confirm = useMutation({
    mutationFn: (d: Draft) =>
      insurerReceiptsService.confirm({
        line: d.line.id,
        insurer: d.insurer,
        auto_tds: d.autoTds,
        allocations: d.rows.filter((r) => r.month && num(r.amount) > 0),
      }),
    onSuccess: () => {
      toast.success('Receipt confirmed and posted to the ledger');
      setDraft(null);
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const withReason = useMutation({
    mutationFn: () =>
      reasonFor!.kind === 'ignore'
        ? insurerReceiptsService.ignore(reasonFor!.line, reason)
        : insurerReceiptsService.unmatch(reasonFor!.line, reason).then(() => undefined),
    onSuccess: () => {
      toast.success(reasonFor!.kind === 'ignore' ? 'Credit ignored' : 'Receipt reversed');
      setReasonFor(null);
      setReason('');
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const startMap = (line: QueueRow) => {
    const s = line.suggestion;
    setDraft({
      line,
      insurer: s?.insurer ?? '',
      autoTds: false,
      rows: s && s.allocations.length ? s.allocations.map((a) => ({ ...a, month: monthOf(a.month) })) : [{ month: '', amount: '' }],
    });
  };
  const setRow = (i: number, patch: Partial<AllocationInput>) =>
    setDraft((d) => d && { ...d, rows: d.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  const allocated = draft ? draft.rows.reduce((s, r) => s + num(r.amount), 0) : 0;
  const credit = draft ? num(draft.line.credit) : 0;
  const remainder = credit - allocated;
  const reasonBox = (kind: 'ignore' | 'unmatch', line: string) =>
    reasonFor && reasonFor.kind === kind && reasonFor.line === line ? (
      <div className="mt-2 flex items-end gap-2">
        <Input
          label={kind === 'ignore' ? 'Why is this not insurer money?' : 'Why is this being unmatched?'}
          name="reason"
          value={reason}
          placeholder={kind === 'ignore' ? 'For example interest, refund' : 'Reason'}
          onChange={(e) => setReason(e.target.value)}
        />
        <Button size="sm" disabled={!reason.trim() || withReason.isPending} onClick={() => withReason.mutate()}>
          {kind === 'ignore' ? 'Ignore credit' : 'Reverse receipt'}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setReasonFor(null)}>
          Cancel
        </Button>
      </div>
    ) : null;

  return (
    <div>
      <PageHeader
        title="Insurer receipts"
        subtitle="Which bank credit is which insurer's money, and which months it pays for"
        breadcrumbs={[{ label: 'Reconciliation' }, { label: 'Insurer receipts' }]}
      />
      <ReconciliationTabs />

      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Credits not yet mapped</h3>
        {queue.isError ? (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
            Could not load credits: {getErrorMessage(queue.error)}
          </p>
        ) : (queue.data?.results ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Every credit is mapped.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Age</th>
                  <th className="py-2 pr-4">Narration</th>
                  <th className="py-2 pr-4">UTR</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-4">Suggested insurer</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {queue.data?.results.map((l) => (
                  <tr key={l.id} className="border-t border-gray-100 align-top dark:border-gray-800">
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDate(l.txn_date)}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{l.age_days} days</td>
                    <td className="py-2 pr-4">{l.narration}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{l.reference}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(l.credit)}</td>
                    <td className="py-2 pr-4">
                      {l.suggestion?.insurer ? (
                        <span title={BASIS_LABEL[l.suggestion.basis]}>
                          {l.suggestion.insurer_name} <span className="text-xs text-gray-500">({BASIS_LABEL[l.suggestion.basis]})</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>
                    <td className="py-2 whitespace-nowrap">
                      {canEdit && (
                        <>
                          <Button size="sm" onClick={() => startMap(l)}>
                            Map
                          </Button>{' '}
                          <Button size="sm" variant="secondary" onClick={() => { setReasonFor({ kind: 'ignore', line: l.id }); setReason(''); }}>
                            Ignore
                          </Button>
                          {reasonBox('ignore', l.id)}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {draft && (
        <Card className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Map {formatCurrency(draft.line.credit)} received on {formatDate(draft.line.txn_date)} (UTR {draft.line.reference || 'none'})
          </h3>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <Select
              label="Insurer"
              name="insurer"
              value={draft.insurer}
              placeholder="Choose insurer"
              options={(insurers.data ?? []).map((i) => ({ value: i.id, label: i.name }))}
              onChange={(e) => setDraft({ ...draft, insurer: e.target.value })}
            />
          </div>
          <div className="mt-4 space-y-2">
            {draft.rows.map((r, i) => (
              <div key={i} className="grid items-end gap-3 md:grid-cols-4">
                <Input label="Month" name={`month-${i}`} type="month" value={r.month ? monthOf(r.month) : ''} onChange={(e) => setRow(i, { month: e.target.value })} />
                <Input label="Cash received" name={`amount-${i}`} inputMode="decimal" value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} />
                <Input label="TDS kept by insurer" name={`tds-${i}`} inputMode="decimal" value={r.tds_amount ?? ''} onChange={(e) => setRow(i, { tds_amount: e.target.value })} />
                <Button size="sm" variant="secondary" onClick={() => setDraft({ ...draft, rows: draft.rows.filter((_, j) => j !== i) })}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
            <Button size="sm" variant="secondary" onClick={() => setDraft({ ...draft, rows: [...draft.rows, { month: '', amount: '' }] })}>
              Add month
            </Button>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={draft.autoTds} onChange={(e) => setDraft({ ...draft, autoTds: e.target.checked })} />
              Work out TDS at the insurer&apos;s rate where I left it blank
            </label>
            <span className={remainder < 0 ? 'text-red-600' : 'text-gray-600 dark:text-gray-300'} role="status">
              {remainder < 0
                ? `Over by ${formatCurrency(-remainder)}`
                : `On account: ${formatCurrency(remainder)}`}
            </span>
          </div>
          <div className="mt-4 flex gap-2">
            <Button disabled={!draft.insurer || remainder < 0 || confirm.isPending} onClick={() => confirm.mutate(draft)}>
              Confirm receipt
            </Button>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <Card className="mb-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Position by insurer and month</h3>
          <Select
            label="Insurer"
            name="position_insurer"
            value={positionInsurer}
            options={[{ value: '', label: 'All insurers' }, ...(insurers.data ?? []).map((i) => ({ value: i.id, label: i.name }))]}
            onChange={(e) => setPositionInsurer(e.target.value)}
          />
        </div>
        {(position.data?.on_account ?? []).length > 0 && (
          <ul className="mt-3 text-sm text-gray-700 dark:text-gray-300">
            {position.data?.on_account.map((o) => (
              <li key={o.insurer}>
                On account with {o.insurer_name}: <strong>{formatCurrency(o.amount)}</strong>
              </li>
            ))}
          </ul>
        )}
        {(position.data?.rows ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Nothing to show yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Insurer</th>
                  <th className="py-2 pr-4">Month</th>
                  <th className="py-2 pr-4 text-right">Expected</th>
                  <th className="py-2 pr-4 text-right">Insurer says paid</th>
                  <th className="py-2 pr-4 text-right">Received</th>
                  <th className="py-2 pr-4 text-right">TDS</th>
                  <th className="py-2 pr-4 text-right">Outstanding</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {position.data?.rows.map((r) => (
                  <tr key={`${r.insurer}-${r.month}`} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">{r.insurer_name}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{r.month.slice(0, 7)}</td>
                    <td className="py-2 pr-4 text-right">
                      {r.expected ? formatCurrency(r.expected) : '-'}
                      {r.expected_source && <span className="ml-1 text-xs text-gray-500">({r.expected_source})</span>}
                    </td>
                    <td className="py-2 pr-4 text-right">{r.claimed ? formatCurrency(r.claimed) : '-'}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(r.received)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(r.tds)}</td>
                    <td className="py-2 pr-4 text-right">{r.outstanding != null ? formatCurrency(r.outstanding) : '-'}</td>
                    <td className="py-2 space-x-1">
                      {num(r.unexplained_short) > 0 && <Badge variant="warning" size="sm">Short, not TDS</Badge>}
                      {num(r.over_received) > 0 && <Badge variant="info" size="sm">Over received</Badge>}
                      {r.claim_mismatch && <Badge variant="error" size="sm">Insurer&apos;s claim differs from bank</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Confirmed receipts</h3>
        {(receipts.data?.results ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No receipts yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Received</th>
                  <th className="py-2 pr-4">Insurer</th>
                  <th className="py-2 pr-4">UTR</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-4 text-right">TDS</th>
                  <th className="py-2 pr-4 text-right">On account</th>
                  <th className="py-2 pr-4">Ledger entry</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {receipts.data?.results.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 align-top dark:border-gray-800">
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDate(r.received_on)}</td>
                    <td className="py-2 pr-4">{r.insurer_name}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{r.reference}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(r.amount)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(r.tds_total)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(r.on_account)}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{r.journal_entry_number}</td>
                    <td className="py-2">
                      {r.status === 'active' ? (
                        <>
                          <Badge variant="success" size="sm">Posted</Badge>{' '}
                          {canEdit && (
                            <Button size="sm" variant="secondary" onClick={() => { setReasonFor({ kind: 'unmatch', line: r.bank_line }); setReason(''); }}>
                              Unmatch
                            </Button>
                          )}
                          {reasonBox('unmatch', r.bank_line)}
                        </>
                      ) : (
                        <Badge variant="default" size="sm">Reversed{r.reversal_reason ? `: ${r.reversal_reason}` : ''}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
